/**
 * Helpers shared by the Stammdaten page and its form dialog.
 *
 * The interesting part is date handling: CMDBuild hands dates back in whichever
 * shape the underlying column has ('2026-08-19', '19/08/2026 00:00:00', epoch
 * millis, full ISO), while `<input type="date">` accepts *only* 'YYYY-MM-DD' and
 * `<input type="datetime-local">` only 'YYYY-MM-DDTHH:mm'. Feeding an unconverted
 * value to either silently renders an EMPTY input — which then submits as "clear
 * this field" and destroys the stored date. `toDateInput` / `toDateTimeInput` are
 * therefore mandatory on every date field, and they return '' only when the value
 * really is unusable.
 */

import type { CmdbAttribute, CmdbCardRow, CmdbClassMeta } from '../../lib/types';

/** How many attribute columns the list table shows before the detail row. */
export const LIST_COLUMNS = 5;

/** 'dd/MM/yyyy[ HH:mm[:ss]]' — CMDBuild's localised shape. */
const DMY = /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

function parts(value: unknown): { date: string; time: string } | null {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number' || /^\d{12,}$/.test(String(value))) {
    const millis = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(millis)) return null;
    const parsed = new Date(millis);
    if (Number.isNaN(parsed.getTime())) return null;
    // Local time on purpose: the input shows what the user's clock shows.
    const pad = (n: number): string => String(n).padStart(2, '0');
    return {
      date: `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`,
      time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`,
    };
  }

  const text = String(value).trim();
  const dmy = DMY.exec(text);
  if (dmy) {
    const [, dd, mm, yyyy, hh = '', min = ''] = dmy;
    return { date: `${yyyy}-${mm}-${dd}`, time: hh && min ? `${hh}:${min}` : '' };
  }
  const iso = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(text);
  if (iso) {
    const [, date = '', hh, min] = iso;
    return { date, time: hh && min ? `${hh}:${min}` : '' };
  }
  return null;
}

/** 'YYYY-MM-DD' for `<input type="date">`, or '' when unparsable. */
export function toDateInput(value: unknown): string {
  return parts(value)?.date ?? '';
}

/** 'YYYY-MM-DDTHH:mm' for `<input type="datetime-local">`, or '' when unparsable. */
export function toDateTimeInput(value: unknown): string {
  const split = parts(value);
  if (!split) return '';
  return `${split.date}T${split.time || '00:00'}`;
}

/** The form value for one attribute of a card (or the empty value for a new one). */
export function initialValue(attr: CmdbAttribute, row: CmdbCardRow | null): string | boolean {
  const raw = row?.values[attr.name];
  switch (attr.type) {
    case 'boolean':
      return raw === true;
    case 'date':
      return toDateInput(raw);
    case 'dateTime':
      return toDateTimeInput(raw);
    case 'reference':
    case 'lookup':
      return raw === null || raw === undefined ? '' : String(raw);
    default:
      return raw === null || raw === undefined ? '' : String(raw);
  }
}

/** All form values for a class, ready for `useState`. */
export function initialForm(
  meta: CmdbClassMeta,
  row: CmdbCardRow | null,
): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const attr of meta.attributes) out[attr.name] = initialValue(attr, row);
  return out;
}

/**
 * Turn form state into the request body.
 *
 * System attributes are skipped (the server drops them anyway, and sending them
 * would make the diff misleading). References are sent as numbers, everything
 * else as the string the input produced — the server coerces and validates.
 */
export function toRequestBody(
  meta: CmdbClassMeta,
  form: Record<string, string | boolean>,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const attr of meta.attributes) {
    if (attr.system) continue;
    const value = form[attr.name];
    if (attr.type === 'boolean') {
      body[attr.name] = value === true;
    } else if (attr.type === 'reference') {
      const text = String(value ?? '').trim();
      body[attr.name] = text === '' ? '' : Number(text);
    } else {
      body[attr.name] = String(value ?? '');
    }
  }
  return body;
}

/** The attributes shown as table columns: the first few editable, non-text ones. */
export function listAttributes(meta: CmdbClassMeta): CmdbAttribute[] {
  const preferred = meta.attributes.filter((a) => a.type !== 'text');
  return (preferred.length ? preferred : meta.attributes).slice(0, LIST_COLUMNS);
}

/** Every class a form for `meta` needs picker options for. */
export function referenceTargets(meta: CmdbClassMeta): string[] {
  const out = new Set<string>();
  for (const attr of meta.attributes) {
    if (attr.type === 'reference' && attr.target_class && !attr.system) out.add(attr.target_class);
  }
  return [...out];
}

/**
 * The card a reference attribute points at, or `null` when the attribute is not
 * a reference or is unset.
 *
 * `row.values` holds the raw card id for references (`row.display` holds only the
 * resolved German label), which is exactly what a deep link into this page needs.
 */
export function referenceLink(
  attr: CmdbAttribute,
  row: CmdbCardRow,
): { klass: string; id: number } | null {
  if (attr.type !== 'reference' || !attr.target_class) return null;
  const raw = row.values[attr.name];
  if (raw === null || raw === undefined || raw === '') return null;
  const id = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isInteger(id) || id <= 0) return null;
  return { klass: attr.target_class, id };
}
